/**
 * Quotes - the quote book's management page (R28 fork 6, moved 2026-10-29).
 *
 * The module's own page, `/channels/:channelId/modules/quotes`, reached from
 * the gear on the channel's Modules list. It sits in the FrontendModule's
 * `SettingsPage` slot (rendered by `pages/ModuleSettings.tsx`, which supplies
 * the back link and the channel selector). Matt: quote management belongs on
 * the module's page, NOT on the channel overview - the `ChannelPanel` slot is
 * for live actions during a stream (the song-request queue), not for managing
 * a collection. Search, add, edit (text / who / game), delete, export. Chat
 * adds and deletes too (!addquote, !delquote); the page polls so those show.
 *
 * Numbers are the server's: unique, never reused, and a delete leaves a gap
 * (quotes_002). The list is newest number first, a page at a time.
 *
 * `.btn` has no disabled style in the design system (index.css), so the
 * buttons here carry their own `disabled:` classes, as QueuePanel's do.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Pencil,
  Plus,
  Quote as QuoteIcon,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import type { ModuleSettingsProps } from '../types'
import { downloadJson, quotesApi, type Quote, type QuoteList } from './api'

const PAGE = 20
const POLL_MS = 15000
const SEARCH_DEBOUNCE_MS = 300
// Mirrors the server's caps (backend/parsing.py, routes.py).
const MAX_TEXT = 400
const MAX_WHO = 100
const MAX_GAME = 200

const DISABLED = 'disabled:opacity-40 disabled:pointer-events-none'

function when(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

interface Draft {
  text: string
  attribution: string
  game: string
}

const EMPTY: Draft = { text: '', attribution: '', game: '' }

/** The text / who / game fields, shared by add and edit. */
function QuoteFields({
  draft,
  onChange,
  gamePlaceholder,
  idPrefix,
}: {
  draft: Draft
  onChange: (d: Draft) => void
  gamePlaceholder: string
  idPrefix: string
}) {
  return (
    <div className="space-y-2">
      <div>
        <label className="label" htmlFor={`${idPrefix}-text`}>Quote</label>
        <textarea
          id={`${idPrefix}-text`}
          className="textarea"
          rows={2}
          maxLength={MAX_TEXT}
          value={draft.text}
          onChange={(e) => onChange({ ...draft, text: e.target.value })}
        />
        <p className="help-text">{draft.text.length}/{MAX_TEXT}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor={`${idPrefix}-who`}>Who said it</label>
          <input
            id={`${idPrefix}-who`}
            className="input"
            maxLength={MAX_WHO}
            placeholder="Optional"
            value={draft.attribution}
            onChange={(e) => onChange({ ...draft, attribution: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor={`${idPrefix}-game`}>Game</label>
          <input
            id={`${idPrefix}-game`}
            className="input"
            maxLength={MAX_GAME}
            placeholder={gamePlaceholder}
            value={draft.game}
            onChange={(e) => onChange({ ...draft, game: e.target.value })}
          />
        </div>
      </div>
    </div>
  )
}

export function QuotesPanel({ channelId }: ModuleSettingsProps) {
  const { success, error } = useToast()
  const [data, setData] = useState<QuoteList | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [adding, setAdding] = useState(false)
  const [addDraft, setAddDraft] = useState<Draft>(EMPTY)
  const [editing, setEditing] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY)
  const alive = useRef(true)

  const refresh = useCallback(async () => {
    try {
      const list = await quotesApi.list(channelId, { search: search || undefined, limit: PAGE, offset })
      if (!alive.current) return
      setData(list)
      setLoadError(null)
    } catch (err) {
      // Only a FIRST load failure is shown; a failed poll keeps the last view.
      if (alive.current) setLoadError(apiErrorMessage(err, 'Could not load the quotes'))
    }
  }, [channelId, search, offset])

  useEffect(() => {
    alive.current = true
    refresh()
    const poll = setInterval(refresh, POLL_MS)
    return () => {
      alive.current = false
      clearInterval(poll)
    }
  }, [refresh])

  // Debounce the search box; a new search starts at the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setOffset(0)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  const act = async (key: string, call: () => Promise<unknown>, ok: string | null, fail: string): Promise<boolean> => {
    setBusy(key)
    try {
      await call()
      if (ok) success(ok)
      return true
    } catch (err) {
      error(apiErrorMessage(err, fail))
      return false
    } finally {
      setBusy(null)
      refresh()
    }
  }

  const handleAdd = async () => {
    const text = addDraft.text.trim()
    if (!text) return
    const ok = await act(
      'add',
      async () => {
        const added = await quotesApi.add(channelId, {
          text,
          attribution: addDraft.attribution.trim() || null,
          // Blank game: leave it out, so the server looks up the category.
          ...(addDraft.game.trim() ? { game: addDraft.game.trim() } : {}),
        })
        success(`Added #${added.quote_number}`)
      },
      null,
      'Could not add that quote',
    )
    if (ok) {
      setAddDraft(EMPTY)
      setAdding(false)
      setOffset(0)
    }
  }

  const startEdit = (q: Quote) => {
    setEditing(q.quote_number)
    setEditDraft({ text: q.text, attribution: q.attribution ?? '', game: q.game ?? '' })
  }

  const handleSave = async (q: Quote) => {
    const text = editDraft.text.trim()
    if (!text) return
    const ok = await act(
      `edit-${q.quote_number}`,
      () =>
        quotesApi.update(channelId, q.quote_number, {
          text,
          attribution: editDraft.attribution.trim() || null,
          game: editDraft.game.trim() || null,
        }),
      `Saved #${q.quote_number}`,
      'Could not save that quote',
    )
    if (ok) setEditing(null)
  }

  const handleDelete = (q: Quote) => {
    if (!confirm(`Delete quote #${q.quote_number}? Its number won't be used again.`)) return
    act(
      `delete-${q.quote_number}`,
      () => quotesApi.remove(channelId, q.quote_number),
      `Deleted #${q.quote_number}`,
      'Could not delete that quote',
    )
  }

  const handleExport = () =>
    act(
      'export',
      async () => {
        const all = await quotesApi.exportAll(channelId)
        downloadJson(all, `quotes-${channelId}.json`)
      },
      'Quotes exported',
      'Could not export the quotes',
    )

  const total = data?.total ?? 0
  const page = Math.floor(offset / PAGE) + 1
  const pages = Math.max(1, Math.ceil(total / PAGE))

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <QuoteIcon size={20} />
          Quotes
          {data && !search && <span className="badge-neutral">{total}</span>}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAdding((a) => !a)}
            disabled={busy !== null}
            className={`btn-secondary btn-sm flex items-center gap-2 ${DISABLED}`}
          >
            {adding ? <X size={14} /> : <Plus size={14} />}
            {adding ? 'Cancel' : 'Add'}
          </button>
          <button
            onClick={handleExport}
            disabled={busy !== null || !data}
            className={`btn-ghost btn-sm flex items-center gap-2 ${DISABLED}`}
            title="Download every quote as JSON"
          >
            {busy === 'export' ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
            Export
          </button>
        </div>
      </div>

      {adding && (
        <div className="rounded-lg bg-surface-app p-3 mb-4 space-y-3">
          <QuoteFields
            draft={addDraft}
            onChange={setAddDraft}
            gamePlaceholder="Blank: the channel's current category"
            idPrefix="quote-add"
          />
          <div className="flex justify-end">
            <button
              onClick={handleAdd}
              disabled={busy !== null || !addDraft.text.trim()}
              className={`btn-primary btn-sm flex items-center gap-2 ${DISABLED}`}
            >
              {busy === 'add' ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              Add quote
            </button>
          </div>
        </div>
      )}

      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          className="input pl-9"
          placeholder="Search text or who said it"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search quotes"
        />
      </div>

      {!data && loadError && <p className="text-sm text-state-danger">{loadError}</p>}
      {!data && !loadError && (
        <div className="flex justify-center py-6">
          <Loader2 className="animate-spin text-text-muted" size={24} />
        </div>
      )}

      {data && (
        <>
          {data.quotes.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">
              {search ? `No quote matches "${search}".` : 'No quotes yet. Viewers add them with !addquote.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {data.quotes.map((q) => {
                const rowBusy = busy === `edit-${q.quote_number}` || busy === `delete-${q.quote_number}`
                if (editing === q.quote_number) {
                  return (
                    <li key={q.id} className="rounded bg-surface-base px-3 py-3 space-y-3">
                      <p className="text-xs font-mono text-text-muted">#{q.quote_number}</p>
                      <QuoteFields
                        draft={editDraft}
                        onChange={setEditDraft}
                        gamePlaceholder="None"
                        idPrefix={`quote-edit-${q.quote_number}`}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditing(null)}
                          disabled={rowBusy}
                          className={`btn-ghost btn-sm ${DISABLED}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSave(q)}
                          disabled={busy !== null || !editDraft.text.trim()}
                          className={`btn-primary btn-sm flex items-center gap-2 ${DISABLED}`}
                        >
                          {rowBusy && <Loader2 className="animate-spin" size={14} />}
                          Save
                        </button>
                      </div>
                    </li>
                  )
                }
                const meta = [q.game, when(q.created_at), `added by ${q.added_by.username}`].filter(Boolean)
                return (
                  <li key={q.id} className="flex items-start gap-3 rounded bg-surface-base px-3 py-2">
                    <span className="w-10 shrink-0 text-right text-xs font-mono text-text-muted pt-0.5">
                      #{q.quote_number}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-text-primary break-words">
                        “{q.text}”{q.attribution && <span className="text-text-secondary"> — {q.attribution}</span>}
                      </p>
                      <p className="text-xs text-text-muted">{meta.join(' · ')}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {rowBusy && <Loader2 className="animate-spin text-text-muted" size={14} />}
                      <button
                        onClick={() => startEdit(q)}
                        disabled={busy !== null || editing !== null}
                        className={`btn-ghost btn-sm px-1.5 ${DISABLED}`}
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(q)}
                        disabled={busy !== null}
                        className={`btn-ghost btn-sm px-1.5 hover:text-state-danger ${DISABLED}`}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {total > PAGE && (
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
                disabled={offset === 0}
                className={`btn-ghost btn-sm flex items-center gap-1 ${DISABLED}`}
              >
                <ChevronLeft size={14} /> Newer
              </button>
              <span className="help-text mt-0">
                Page {page} of {pages}
                {search && ` · ${total} match${total === 1 ? '' : 'es'}`}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE)}
                disabled={offset + PAGE >= total}
                className={`btn-ghost btn-sm flex items-center gap-1 ${DISABLED}`}
              >
                Older <ChevronRight size={14} />
              </button>
            </div>
          )}
          <p className="help-text mt-3">
            Numbers are never reused: deleting a quote leaves a gap. Who may use !quote, !addquote and !delquote
            is set on the Commands page.
          </p>
        </>
      )}
    </div>
  )
}

export default QuotesPanel
